import Chart from 'chart.js/auto';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { normalizeUnit } from '../utils/unitFormatter';


class ChartComponent {
    constructor() {
        this.selectedFacilityId = null;
        this.chartData = null;
        this.loading = false;
        this.graphUnit = '';
        this.chartInstance = null;
        
        this.init();
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupChart());
        } else {
            this.setupChart();
        }
    }

    setupChart() {
        const container = document.getElementById('chart-container');
        if (!container) {
            console.warn('Chart container not found');
            return;
        }

        // Get facility ID from data attribute
        const facilityId = container.dataset.facilityId || container.getAttribute('data-facility-id');
        if (facilityId) {
            this.selectedFacilityId = facilityId;
        }

        // Create the chart section HTML without inline styles
        container.innerHTML = `
            <div class="facility-chart-section">
                <!--<h3>Facility Metrics Chart</h3> -->
                
                <!-- Loading State -->
                <div id="loading-state" class="loading-state">
                    <p>Loading chart data...</p>
                </div>

                <!-- Chart Container -->
                <div id="chart-canvas-container" class="chart-canvas-container">
                    <canvas id="planet-chart" class="chart-canvas"></canvas>
                    
                    <div id="chart-placeholder" class="chart-placeholder">
                        Chart will display facility metrics data
                    </div>
                </div>
            </div>
        `;

        // Auto-load chart if facility ID is provided
        if (this.selectedFacilityId) {
            // Add small delay to ensure DOM is fully ready
            setTimeout(() => {
                this.getChartPreview(this.selectedFacilityId);
            }, 100);
        } else {
            // If no facility ID provided, show ready state
            console.log('Chart component ready - no facility ID provided');
        }
    }

    async getChartPreview(facId) {
        if (!facId) return;
        
        this.setLoading(true);
        
        const d = new Date();
        const startDate = d.setDate(d.getDate() - 7);
        const endDate = Date.now();

        const startQuery = new Date(startDate).toISOString().replace("Z", "");
        const endQuery = new Date(endDate).toISOString().replace("Z", "");

        try {
            const response = await fetch(
                `/wp-json/lixar/v1/metric-logs?facilityId=${facId}&startDate=${startQuery}&endDate=${endQuery}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            const res = await response.json();
            
            if (res.status === 200 && res.data && res.data.length > 0) {
                const rawUnit = res.data[0].data && res.data[0].data[0] ? res.data[0].data[0].unit : '';
                const unit = normalizeUnit(rawUnit);
                this.graphUnit = unit;

                const planetChartData = {
                    type: "line",
                    data: {
                        datasets: [
                            {
                                label: "",
                                data: [],
                                fill: false,
                                borderColor: "#3581f7",
                                pointRadius: 1,
                                pointHoverRadius: 5,
                                borderWidth: 1,
                            },
                        ],
                    },
                    options: {
                        responsive: true,
                        plugins: {
                            legend: {
                                display: false,
                            },
                            title: {
                                display: false,
                                text: "Facility Metrics Chart",

                            },
                            tooltip: {
                                callbacks: {
                                    label: function (context) {
                                        let label = context.dataset.label || "";
                                        if (label) {
                                            label += ": ";
                                        }
                                        label += context.parsed.y + " " + unit;
                                        return label;
                                    },
                                },
                            },
                        },
                        scales: {
                            x: {
                                display: true,
                                type: "time",
                                time: {
                                    parser: "MM/DD/YYYY HH:mm",
                                    tooltipFormat: "ll HH:mm",
                                    unit: "day",
                                    stepSize: 1,
                                    displayFormats: {
                                        day: "MMM DD",
                        
                                    },
                                },
                            },
                            y: {
                                display: true,
                                ticks: {
                                    callback: function (value) {
                                        return value.toFixed(2) + " " + unit;
                                    },
                                    stepSize: 1,
                                },
                            },
                        },
                        onHover: function (event, elements) {
                            console.log("Chart hover:", event, "Elements:", elements);
                        },
                    },
                };

                // Map data points
                planetChartData.data.datasets[0].data = res.data[0].data.map((e) => {
                    return {
                        x: moment(new Date(e.time)).format("MM/DD/YYYY HH:mm:ss"),
                        y: parseFloat(e.value),
                    };
                });

                this.chartData = planetChartData;
                
                // Render chart
                setTimeout(() => {
                    this.renderChart(planetChartData);
                }, 100);

            } else {
                console.log('No data available for this facility');
                this.chartData = null;
                this.hideChart();
            }
        } catch (error) {
            console.error('Error fetching chart data:', error);
            this.chartData = null;
            this.hideChart();
        } finally {
            this.setLoading(false);
        }
    }

    renderChart(planetChartData) {
        const canvas = document.getElementById('planet-chart');
        if (canvas && planetChartData) {
            // Destroy existing chart if it exists
            if (this.chartInstance) {
                this.chartInstance.destroy();
            }

            const ctx = canvas.getContext('2d');
            this.chartInstance = new Chart(ctx, planetChartData);
            
            // Show chart and hide placeholder
            canvas.style.display = 'block';
            const placeholder = document.getElementById('chart-placeholder');
            if (placeholder) {
                placeholder.style.display = 'none';
            }
        }
    }

    hideChart() {
        const canvas = document.getElementById('planet-chart');
        const placeholder = document.getElementById('chart-placeholder');
        
        if (canvas) {
            canvas.style.display = 'none';
        }
        
        if (placeholder) {
            placeholder.style.display = 'flex';
            placeholder.innerHTML = 'No data available for this facility';
        }
    }

    handleFacilitySelect(facilityId) {
        this.selectedFacilityId = facilityId;
        
        if (facilityId) {
            this.getChartPreview(facilityId);
        } else {
            // Reset to placeholder
            this.hideChart();
            const placeholder = document.getElementById('chart-placeholder');
            if (placeholder) {
                placeholder.innerHTML = 'Chart will display facility metrics data';
            }
        }
    }

    setLoading(isLoading) {
        this.loading = isLoading;
        const loadingElement = document.getElementById('loading-state');
        const chartContainer = document.getElementById('chart-canvas-container');
        
        if (loadingElement && chartContainer) {
            if (isLoading) {
                loadingElement.style.display = 'block';
                chartContainer.style.display = 'none';
            } else {
                loadingElement.style.display = 'none';
                chartContainer.style.display = 'block';
            }
        }
    }

    destroy() {
        if (this.chartInstance) {
            this.chartInstance.destroy();
            this.chartInstance = null;
        }
    }

    // Public method to load chart with facility ID
    loadChart(facilityId) {
        if (facilityId) {
            this.selectedFacilityId = facilityId;
            this.getChartPreview(facilityId);
        } else {
            console.warn('No facility ID provided to loadChart method');
        }
    }

    // Public method to get current facility ID
    getCurrentFacilityId() {
        return this.selectedFacilityId;
    }
}

// Export for use in main.js
window.ChartComponent = ChartComponent;

export default ChartComponent;